import {
  type AtLeast,
  type Block,
  type ListView,
  compareOrder,
} from '@basehub/data/replicache/index'
import { SortDirection } from '@basehub/data/replicache/schema/block/search'
import { invariant } from '@epic-web/invariant'

const ORDER_SEPARATOR = '__br_order__'

export type SortValue = { value: string; type: SortDirection }

export const compare = (a: SortValue[], b: SortValue[]) => {
  for (let i = 0; i < a.length; i++) {
    const aValue = a[i]
    const bValue = b[i]
    invariant(aValue, 'aValue should be defined')
    invariant(bValue, 'bValue should be defined')
    if (aValue.value === bValue.value) continue

    // Check if both values can be parsed as numbers
    const aNum = Number(aValue.value)
    const bNum = Number(bValue.value)
    const areNumbers = !isNaN(aNum) && !isNaN(bNum)
    const aOrder = aValue.value.split(ORDER_SEPARATOR)[1]
    const bOrder = bValue.value.split(ORDER_SEPARATOR)[1]
    const areOrders = !!aOrder && !!bOrder

    if (aValue.type === 'asc') {
      if (areNumbers) {
        if (aNum < bNum) return -1
        if (aNum > bNum) return 1
      } else if (areOrders) {
        if (compareOrder(aOrder, bOrder) < 0) return -1
        if (compareOrder(aOrder, bOrder) > 0) return 1
      } else {
        if (aValue.value < bValue.value) return -1
        if (aValue.value > bValue.value) return 1
      }
    } else {
      if (areNumbers) {
        if (aNum < bNum) return 1
        if (aNum > bNum) return -1
      } else if (areOrders) {
        if (compareOrder(aOrder, bOrder) < 0) return 1
        if (compareOrder(aOrder, bOrder) > 0) return -1
      } else {
        if (aValue.value < bValue.value) return 1
        if (aValue.value > bValue.value) return -1
      }
    }
  }
  return 0
}

export const getSortValues = ({
  brOrder,
  block,
  sort,
  getBlockByInstanceChildTarget,
}: {
  brOrder: string
  block: AtLeast<Block, 'type' | 'value' | 'title' | 'createdAt' | 'updatedAt'>
  sort: ListView['sort']
  getBlockByInstanceChildTarget: (instanceChildTarget: string) => Block | null
}): SortValue[] => {
  const sortValues: SortValue[] = []

  sort.forEach((s) => {
    switch (s.by) {
      case '__title': {
        sortValues.push({ value: block.title, type: s.type })
        break
      }
      case '__createdAt': {
        sortValues.push({ value: block.createdAt, type: s.type })
        break
      }
      case '__updatedAt': {
        sortValues.push({
          value: block.updatedAt || block.createdAt,
          type: s.type,
        })
        break
      }
      default: {
        // it's a column
        const childBlock = getBlockByInstanceChildTarget(s.by)
        if (!childBlock) break
        switch (childBlock.type) {
          case 'icon':
          case 'date':
          case 'color':
          case 'text':
            sortValues.push({
              value: childBlock.value || '',
              type: s.type,
            })
            break
          case 'select':
            sortValues.push({
              value: childBlock.value.join(''),
              type: s.type,
            })
            break
          case 'code-snippet':
            sortValues.push({
              value: childBlock.value.code,
              type: s.type,
            })
            break
          case 'number':
            sortValues.push({
              value: String(childBlock.value || 0),
              type: s.type,
            })
            break
          case 'boolean':
            sortValues.push({
              value: childBlock.value ? '1' : '0',
              type: s.type,
            })
            break
          default:
            // sort not supported for this block
            break
        }
      }
    }
  })

  if (sortValues.length === 0) {
    sortValues.push({ value: ORDER_SEPARATOR + brOrder, type: 'asc' })
  }

  return sortValues
}
